import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { OwnerWithContacts } from "./useOwners";
import { useCurrentClinicCustomerId } from "@/hooks/useCurrentClinicCustomerId";
import { useHasPermissionSync } from "@/hooks/usePermissions";

/**
 * Hook para buscar owners por nome, email, CPF/documento ou telefone
 * Usado na página de busca de responsável antes de criar paciente
 */
export const useOwnerSearch = (searchTerm: string) => {
  const { data: customerId } = useCurrentClinicCustomerId();
  const { hasPermission: canViewPatients, isLoading: isLoadingPermission } = useHasPermissionSync("patients", "view");
  const trimmed = searchTerm.trim();

  return useQuery<OwnerWithContacts[]>({
    queryKey: ["owner-search", customerId, searchTerm],
    enabled: canViewPatients && !isLoadingPermission && !!customerId,
    queryFn: async () => {
      if (!customerId) return [];

      const isEmptySearch = trimmed.length === 0;

      // Empty search: first 10 owners alphabetically (RPC orders by owner_name, pet_name when p_search is null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: matchRows, error: matchError } = await (supabase as any).rpc("get_owners_with_pets", {
        p_customer_id: customerId,
        p_search: isEmptySearch ? null : trimmed,
        p_offset: 0,
        p_limit: isEmptySearch ? 50 : 200,
      });

      if (matchError) throw matchError;

      const rows = (matchRows as any[] | null | undefined) ?? [];
      // Dedupe by owner_id preserving order; take first 10 for empty search, all for search
      const seen = new Set<string>();
      const ownerIds: string[] = [];
      for (const row of rows) {
        const id = row?.owner_id;
        if (id && !seen.has(id)) {
          seen.add(id);
          ownerIds.push(id);
          if (isEmptySearch && ownerIds.length >= 10) break;
        }
      }

      if (ownerIds.length === 0) return [];

      // Buscar os owners completos (com contatos + enderecos) para permitir preencher o form ao selecionar.
      const { data: ownersData, error: ownersError } = await supabase
        .from("owners")
        .select(
          `
          *,
          owner_customer:owners_customers!inner(
            customer_id,
            deleted_at
          ),
          phones:owners_phones(*),
          emails:owners_emails(*),
          addresses:owners_addresses(*, city:cities(id, name, state_id, state:states(id, name, code)))
        `,
        )
        .eq("owner_customer.customer_id", customerId)
        .is("owner_customer.deleted_at", null)
        .is("deleted_at", null)
        .in("id", ownerIds)
        .order("name", { ascending: true })
        .limit(isEmptySearch ? 10 : 20);

      if (ownersError) throw ownersError;

      // Normalizar resultado (remover owner_customer, manter contatos/endereco)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (ownersData as any[] | null | undefined)?.map((owner: any) => {
        const { owner_customer, ...rest } = owner;
        return {
          ...rest,
          phones: owner.phones || [],
          emails: owner.emails || [],
          addresses: owner.addresses || [],
        } as OwnerWithContacts;
      }) ?? [];
    },
    staleTime: 1000 * 30, // Cache por 30 segundos
  });
};
